<template>
  <Flex vld-parent column px-3>
    <loading
      :active.sync="isMainChartLoading"
      :can-cancel="false"
      :is-full-page="false"
      background-color="#f0f0f0"
      loader="bars"
      v-bind:height="0"
      v-bind:width="0"
    >
    </loading>
    <Flex column grow overflow-y-auto>
      <Flex column options-block>
        <div v-if="isTitleInputVisible($route.name)">
          <Flex options-header>{{ $t('options.title') }}</Flex>
          <TextField
            class="mb-1"
            :value.sync="modifiedTitleText"
            :placeholder="$t('options.title.inputplaceholder')"
            :rules="[(v) => v.length < maxTitleLength || 'validationError']"
            @blur="onBlur"
          />
        </div>
        <div>
          <Flex options-header>{{ $t('label.chart.type') }}</Flex>
          <Flex v-for="(chartType, index) in chartTypes" :key="index" options-item>
            <ChartTypeSelector :chartType="chartType" />
          </Flex>
        </div>
        <div v-if="isCategorySelectorVisible($route.name)">
          <Flex options-header>{{ $t('label.category.xaxis') }}</Flex>
          <Flex v-for="(category, index) in categoriesByMeasure()" :key="index" options-item>
            <CategorySelector :category="category"></CategorySelector>
          </Flex>
        </div>
        <div v-if="isMeasureSingleSelectorVisible($route.name)">
          <Flex options-header relative>
            {{ $t('label.category.yaxis') }}
            <CalculatedMeasureSelector :iconToShow="'add'" />
          </Flex>
          <Flex v-for="(measure, index) in measuresByCategoryAndSeries()" :key="index" options-item>
            <MeasureSingleSelector :measure="measure" />
          </Flex>
          <Flex
            v-for="(calcMeasure, index) in allCalculatedMeasures"
            :key="'A' + index"
            options-item
            relative
          >
            <CalculatedMeasureSelector
              :iconToShow="'edit'"
              :calcMeasure="calcMeasure"
              :usedAlone="true"
            />
          </Flex>
        </div>
        <div v-if="isMeasureMultiSelectorVisible($route.name)">
          <Flex options-header relative>
            {{ $t('label.category.yaxis') }}
            <CalculatedMeasureSelector :iconToShow="'add'" />
          </Flex>
          <Flex v-for="(measure, index) in measuresByCategoryAndSeries()" :key="index" options-item>
            <MeasureMultiSelector :measure="measure" :allMeasures="allMeasures" />
          </Flex>
          <Flex
            v-for="(calcMeasure, index) in allCalculatedMeasures"
            :key="'A' + index"
            options-item
            relative
          >
            <CalculatedMeasureSelector :iconToShow="'edit'" :calcMeasure="calcMeasure" />
          </Flex>
        </div>
        <div
          v-if="isGeoSelectorVisible($route.name) && isLocationSelectorVisible()"
          class="location-options"
        >
          <Flex options-header relative>
            {{ $t('label.category.locationName') }}
            <ClearSelector :selectedValue="location" :attributeToClear="'location'"></ClearSelector>
          </Flex>
          <Flex v-for="(locationCategory, index) in locationCategories" :key="index" options-item>
            <LocationSelector :locationCategory="locationCategory"></LocationSelector>
          </Flex>
        </div>
        <div v-if="isGeoSelectorVisible($route.name) && containsSelectableGeoCategories()">
          <Flex options-header>{{ $t('label.category.latitude') }}</Flex>
          <Flex v-for="(geoCategory, index) in geoCategories" :key="index" options-item>
            <GeoSelector :geoCategory="geoCategory" :geoType="'geoTypeLatitude'"></GeoSelector>
          </Flex>
        </div>
        <div v-if="isGeoSelectorVisible($route.name) && containsSelectableGeoCategories()">
          <Flex options-header>{{ $t('label.category.longitude') }}</Flex>
          <Flex v-for="(geoCategory, index) in geoCategories" :key="index" options-item>
            <GeoSelector :geoCategory="geoCategory" :geoType="'geoTypeLongitude'"></GeoSelector>
          </Flex>
        </div>
        <div v-if="isGeoSelectorVisible($route.name)">
          <Flex options-header>{{ $t('label.category.sizeby') }}</Flex>
          <Flex v-for="(measure, index) in sizeByMeasures" :key="index" options-item>
            <MeasureSingleSelector :measure="measure"></MeasureSingleSelector>
          </Flex>
        </div>
        <div v-if="isSeriesSelectorVisible($route.name)" class="series-options">
          <Flex options-header relative>
            {{ $t('label.category.series') }}
            <ClearSelector :selectedValue="selectedSeries" :attributeToClear="'selectedSeries'">
            </ClearSelector>
          </Flex>
          <Flex v-for="(singleSeries, index) in seriesByMeasure()" :key="index" options-item>
            <SeriesSelector :series="singleSeries"></SeriesSelector>
          </Flex>
        </div>
        <div v-if="isWordCloudKeywordSelectorVisible($route.name, wordCloudColumns)">
          <Flex options-header relative>
            {{ $t('label.keywordFields') }}
          </Flex>
          <Flex v-for="(wordCloudColumn, index) in wordCloudColumns" :key="index" options-item>
            <WordCloudColumnSelector :wordCloudColumn="wordCloudColumn"></WordCloudColumnSelector>
          </Flex>
          <Flex options-header relative>
            {{ $t('label.options') }}
          </Flex>
          <Flex options-item>
            <WordCloudNumberSelector :wordCloudColumns="wordCloudColumns"></WordCloudNumberSelector>
          </Flex>
        </div>
      </Flex>
    </Flex>
    <SaveButton v-if="isDevelopment"></SaveButton>
  </Flex>
</template>

<script>
import Flex from '@wizer/core/components/flex/Flex'
import TextField from '@wizer/core/components/text-field/TextField'
import { mapState } from 'vuex'
import { find, isEmpty, map, size } from 'lodash'
import Loading from 'vue-loading-overlay'
import ChartConfigurations from '@/components/list-view-visualisation/chart/ChartConfigurations'
import CategorySelector from '@/components/list-view-visualisation/selector/CategorySelector'
import SeriesSelector from '@/components/list-view-visualisation/selector/SeriesSelector'
import ChartTypeSelector from '@/components/list-view-visualisation/selector/ChartTypeSelector'
import MeasureSingleSelector from '@/components/list-view-visualisation/selector/MeasureSingleSelector'
import MeasureMultiSelector from '@/components/list-view-visualisation/selector/MeasureMultiSelector'
import CalculatedMeasureSelector from '@/components/list-view-visualisation/selector/CalculatedMeasureSelector'
import GeoSelector from '@/components/list-view-visualisation/selector/GeoSelector'
import LocationSelector from '@/components/list-view-visualisation/selector/LocationSelector'
import ClearSelector from '@/components/list-view-visualisation/selector/ClearSelector'
import WordCloudColumnSelector from '@/components/list-view-visualisation/selector/WordCloudColumnSelector'
import IframeMixin from '@/mixins/IframeMixin'
import SelectorMixin from '@/mixins/SelectorMixin'
import 'vue-loading-overlay/dist/vue-loading.css'
import WordCloudNumberSelector from '@/components/list-view-visualisation/selector/WordCloudNumberSelector'
import SaveButton from '@/components/list-view-visualisation/SaveButton'

export default {
  name: 'SideBar',
  mixins: [SelectorMixin, IframeMixin],
  components: {
    SaveButton,
    WordCloudNumberSelector,
    ChartTypeSelector,
    CategorySelector,
    SeriesSelector,
    MeasureSingleSelector,
    MeasureMultiSelector,
    CalculatedMeasureSelector,
    Flex,
    TextField,
    GeoSelector,
    LocationSelector,
    ClearSelector,
    WordCloudColumnSelector,
    Loading,
  },
  props: {
    listId: String,
    category: String,
    selectedSeries: String,
    dateFormat: String,
    level: [String, Number],
    filterQuery: String,
    measures: Array,
    calculatedMeasures: Array,
    smartUrlPrefix: String,
    titleText: String,
    latitude: String,
    longitude: String,
    location: String,
    isMainChartLoading: Boolean,
  },
  data() {
    return {
      chartTypes: map(ChartConfigurations, (config) => {
        return {
          routeName: config.name,
          displayName: this.$t(config.displayNameKey),
        }
      }),
    }
  },
  created() {
    let userListViewId
    if (this.filterQuery) {
      userListViewId = JSON.parse(this.filterQuery).userListViewId
    }
    this.$store.dispatch('fetchAllCategories', {
      listId: this.listId,
      userListViewId,
    })
    this.$store.dispatch('setTitle', this.titleText)
  },

  mounted() {
    this.registerUserSecurityWarningCallback()
  },

  computed: {
    modifiedTitleText: {
      get() {
        return this.storedTitleText
      },
      set(value) {
        this.$store.dispatch('setTitle', value)
      },
    },
    ...mapState({
      categories: (state) => state.ChartCategory.categories,
      series: (state) => state.ChartCategory.series,
      allMeasures: (state) => state.ChartCategory.measures,
      allCalculatedMeasures: (state) => state.ChartCategory.allCalculatedMeasures,
      wordCloudColumns: (state) => state.ChartCategory.wordCloudColumns,
      maxTitleLength: (state) => state.ChartCategory.maxTitleLength,
      storedTitleText: (state) => state.ChartCategory.titleText,
      defaultTitle: (state) => state.ChartCategory.defaultTitle,
      geoCategories: (state) => state.ChartCategory.geoCategories,
      locationCategories: (state) => state.ChartCategory.locationCategories,
      defaultLatitude: (state) => state.ChartCategory.defaultLatitude,
      defaultLongitude: (state) => state.ChartCategory.defaultLongitude,
      defaultLocation: (state) => state.ChartCategory.defaultLocation,
    }),
    sizeByMeasures() {
      if (this.containsSelectableGeoCategories()) {
        return this.allMeasures
      }
      return this.allMeasures
        .filter((measure) => measure.attributePath !== this.defaultLatitude)
        .filter((measure) => measure.attributePath !== this.defaultLongitude)
    },
    isDevelopment() {
      return process.env.NODE_ENV === 'development'
    },
  },

  watch: {
    categories(newCategories, oldCategories) {
      // upon initial load, we want to redirect to default category if no parameters exists
      if (!this.category && !isEmpty(newCategories) && isEmpty(oldCategories)) {
        this.$router.push(
          this.getDefaultRoute(
            this.$route.name,
            this.listId,
            this.filterQuery,
            this.categories[0],
            this.categories[0].subOptions[0],
            this.wordCloudColumns[0],
            this.smartUrlPrefix,
            this.geoCategories,
          ),
        )
      }
    },
  },

  methods: {
    onBlur() {
      if (
        this.$route.query.titleText === this.storedTitleText &&
        this.$route.query.titleText === this.defaultTitle
      ) {
        return
      }
      const newRoute = {
        name: this.$route.name,
        query: { ...this.$route.query },
      }
      newRoute.query.titleText = this.storedTitleText
      newRoute.query.defaultTitleText = this.defaultTitle
      this.$router.push(newRoute).catch(() => {})
    },
    getDefaultRoute(
      routeName,
      listId,
      filterQuery,
      category,
      subOption,
      wordCloudColumn,
      smartUrlPrefix,
      geoCategories,
    ) {
      const route = {
        name: routeName,
        query: {
          filterQuery,
          listId,
          category: category.attributePath,
          measures: ['count()'],
          wordCloudColumns: wordCloudColumn ? [wordCloudColumn.attributePath] : [],
          smartUrlPrefix,
        },
      }
      if (subOption) {
        route.query[subOption.attributeName] = subOption.attributeValue
      }
      if (size(this.geoCategories) > 1) {
        route.query.latitude = this.getDefaultCategory(geoCategories, this.defaultLatitude)
        route.query.longitude = this.getDefaultCategory(geoCategories, this.defaultLongitude)
      }
      if (!isEmpty(this.defaultLocation)) {
        route.query.location = this.defaultLocation
      }
      return route
    },
    getDefaultCategory(categories, defaultValue) {
      const defaultCategory = find(
        categories,
        (category) => category.attributePath === defaultValue,
      )
      if (defaultCategory) {
        return defaultCategory.attributePath
      }
      return categories[0].attributePath
    },
    isThereRestrictedSecurityForAttributePath(items, attributePath) {
      const selectedItem = find(items, (item) => item.attributePath === attributePath)
      if (selectedItem && selectedItem.hasRestrictedSecurity) return true
      return false
    },
    isThereRestrictedSecurity() {
      return (
        this.isThereRestrictedSecurityForAttributePath(this.categories, this.category) ||
        this.isThereRestrictedSecurityForAttributePath(this.measures, this.measure) ||
        this.isThereRestrictedSecurityForAttributePath(this.series, this.selectedSeries) ||
        this.isThereRestrictedSecurityForAttributePath(this.locationCategories, this.location) ||
        this.isThereRestrictedSecurityForAttributePath(this.geoCategories, this.latitude) ||
        this.isThereRestrictedSecurityForAttributePath(this.geoCategories, this.longitude)
      )
    },
    async outputUserSecurityWarning() {
      const notification = {
        type: 'warning',
        message: this.$t('message.otherusenoviewpermission'),
      }
      await this.$store.dispatch('Notification/add', notification, { root: true })
    },
    registerUserSecurityWarningCallback() {
      this.registerListener('chart-sidebar-user-security-warning', () => {
        if (this.isThereRestrictedSecurity()) {
          this.outputUserSecurityWarning()
        }
      })
    },
    categoriesByMeasure() {
      if (this.measures) {
        return this.categories.filter(
          (category) =>
            !this.selectedMeasureAttributes().includes(category.attributePath.split('.')[0]),
        )
      }
      return this.categories
    },
    seriesByMeasure() {
      if (this.measures) {
        return this.series.filter(
          (eachSeries) =>
            !this.selectedMeasureAttributes().includes(eachSeries.attributePath.split('.')[0]),
        )
      }
      return this.series
    },
    selectedMeasureAttributes() {
      return this.measures.map((measure) => {
        const measureAttrb = measure.substring(measure.indexOf('(') + 1, measure.indexOf(')'))
        return measureAttrb.split('.')[0]
      })
    },
    measuresByCategoryAndSeries() {
      const measuresByCategory = this.allMeasures.filter(
        (measure) => !measure.attributePath.includes(this.category && this.category.split('.')[0]),
      )
      if (this.selectedSeries && !isEmpty(this.selectedSeries)) {
        const measuresByCategoryAndSelectedSeries = measuresByCategory.filter(
          (measure) => !measure.attributePath.includes(this.selectedSeries.split('.')[0]),
        )
        return measuresByCategoryAndSelectedSeries
      }
      return measuresByCategory
    },
  },
}
</script>

<style lang="postcss">
.options-block {
  @apply w-64 mb-8 text-xs;
}
.options-header {
  @apply p-2 bg-gray-300 mb-1 font-bold;
}
.options-item {
  @apply px-2 py-1;
}
.router-link-exact-active {
  @apply font-bold text-primary-500;
}
</style>
